import React, {useEffect, useState} from 'react';
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import api from '../../../../api/axios';
import type {User} from '../../../../../types/user/user';
import BaseModal from '../../../../components/utils/BaseModal';

type Props = {
  open: boolean;
  userId: User | null;
  onClose: () => void;
  onSave: (updatedUser: User) => void;
};

type Roles = {
  userRoleId: number;
  roleName: string;
};

const roleTranslations: Record<string, string> = {
  SYSTEM_ADMIN: 'system.user.edit.systemAdmin',
  ADMIN: 'system.user.edit.admin',
  SUPPORT: 'system.user.edit.support',
  USER: 'system.user.edit.user'
};

const UserEditModal: React.FC<Props> = ({open, userId, onClose, onSave}) => {
  const {t} = useTranslation();

  const [formData, setFormData] = useState<User>({
    userId: 0,
    email: '',
    firstName: '',
    lastName: '',
    postalCode: '',
    city: '',
    street1: '',
    street2: '',
    nationality: '',
    region: '',
    iban: '',
    phone: '',
    role: '',
    enabled: false,
    deleted: false,
    deleteReason: '',
    customDeleteReason: '',
    deletedAt: '',
    deletedBy: '',
    mfaEnabled: false
  });

  const [roles, setRoles] = useState<Roles[]>([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const fetchRoles = async () => {
      try {
        const response = await api.get('/user/get-assignable-roles');
        setRoles(response.data);
      } catch {
        // ignore
      }
    };

    fetchRoles();
  }, [open]);

  useEffect(() => {
    if (userId) {
      setFormData(userId);
    }
  }, [userId]);

  const handleRemoveMfa = async () => {
    try {
      await api.delete(`/user/${formData.userId}/remove-mfa-by-admin`);
      toast.success(t('system.user.edit.mfaRemoved'));
      setFormData(prev => ({...prev, mfaEnabled: false}));
    } catch {
      toast.error(t('system.user.edit.mfaRemoveError'));
    }
  };

  const handleChange = (field: keyof User, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    const selectedRoleId =
        roles.find(r => r.roleName === formData.role)?.userRoleId ?? null;

    const payload = {
      email: formData.email,
      firstName: formData.firstName || null,
      lastName: formData.lastName || null,
      postalCode: formData.postalCode || null,
      city: formData.city || null,
      street1: formData.street1 || null,
      street2: formData.street2 || null,
      nationality: formData.nationality || null,
      region: formData.region || null,
      iban: formData.iban || null,
      phone: formData.phone || null,
      userRoleId: selectedRoleId
    };

    const editPromise = api.put(
        `/user/${formData.userId}/edit-user-by-admin`,
        payload
    );

    await toast.promise(editPromise, {
      pending: t('toastify.pending'),
      success: t('system.user.edit.success'),
      error: t('system.user.edit.error')
    });

    await editPromise;
    onSave(formData);
  };

  return (
      <BaseModal
          open={open}
          title={t('system.user.edit.title')}
          abortText={t('system.user.edit.cancel')}
          submitText={t('system.user.edit.save')}
          closeButton
          onAbort={onClose}
          onSubmit={handleSubmit}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
                label={t('system.user.userid')}
                value={formData.userId}
                fullWidth
                disabled
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="role-label">
                {t('system.user.edit.role')}
              </InputLabel>
              <Select
                  labelId="role-label"
                  value={formData.role}
                  label={t('system.user.edit.role')}
                  onChange={(e: SelectChangeEvent) =>
                      handleChange('role', e.target.value)
                  }
              >
                {roles.map(role => (
                    <MenuItem key={role.userRoleId} value={role.roleName}>
                      {roleTranslations[role.roleName]
                          ? t(roleTranslations[role.roleName])
                          : role.roleName}
                    </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
                label={t('system.user.edit.firstName')}
                value={formData.firstName}
                onChange={e => handleChange('firstName', e.target.value)}
                fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
                label={t('system.user.edit.lastName')}
                value={formData.lastName}
                onChange={e => handleChange('lastName', e.target.value)}
                fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
                label={t('system.user.edit.email')}
                value={formData.email}
                onChange={e => handleChange('email', e.target.value)}
                fullWidth
                type="email"
            />
          </Grid>
        </Grid>

        {formData.mfaEnabled && (
            <div className="mt-4">
              <Button variant="contained" color="error" onClick={handleRemoveMfa}>
                {t('system.user.edit.removeMfa')}
              </Button>
            </div>
        )}
      </BaseModal>
  );
};

export default UserEditModal;
